/**
 * Investigations port — the list/index of collaborative investigation
 * workspaces.
 *
 * An investigation is a standalone, team-collaborative workspace (a room in
 * `@authe/collaboration`) hosting the transaction table, graph, semantic view,
 * shared notes and a comment thread. Its *content* lives in the collaborative
 * Yjs room (synced across the team via AppSync, persisted per-browser in
 * IndexedDB); THIS port only tracks the lightweight index — which
 * investigations exist, their title/kind, so a list page can show them.
 *
 * MVP: `createLocalInvestigationsSource` persists the index in `localStorage`,
 * namespaced per tenancy. There is no `investigations` backend yet; cross-user
 * discovery is via shareable `/investigations/$id` links (opening a link joins
 * the same room live). A REST-backed `InvestigationsSource` is a drop-in
 * follow-up — the feature only ever talks to this interface.
 */

/** A saga preset seeds the workspace with a filtered view (e.g. CCIP). */
export type InvestigationSaga = "bridge.ccip" | "bridge.cctp" | null;

export interface Investigation {
  id: string;
  title: string;
  /** Optional saga preset — a workspace pre-filtered to a cross-chain kind. */
  saga: InvestigationSaga;
  /** Optional chain context (numeric chain id). */
  chainId: number | null;
  createdBy: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface CreateInvestigationInput {
  title: string;
  saga?: InvestigationSaga;
  chainId?: number | null;
  createdBy?: string | null;
}

export interface InvestigationsSource {
  list(): Promise<Investigation[]>;
  get(id: string): Promise<Investigation | null>;
  create(input: CreateInvestigationInput): Promise<Investigation>;
  update(investigation: Investigation): Promise<Investigation>;
  remove(id: string): Promise<void>;
}

const STORAGE_PREFIX = "authe-investigations";

function newId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `inv_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

function readStore(key: string): Investigation[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as Investigation[]) : [];
  } catch {
    return [];
  }
}

function writeStore(key: string, rows: Investigation[]): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify(rows));
  } catch {
    // Quota / private-mode failures are non-fatal — the room content still
    // syncs; only the local index is lost.
  }
}

/**
 * `localStorage`-backed investigations index, scoped by `namespace` (the
 * caller's tenancy) so one browser signed into multiple tenants keeps separate
 * lists. Sorted most-recently-updated first.
 */
export function createLocalInvestigationsSource(namespace: string): InvestigationsSource {
  const key = `${STORAGE_PREFIX}:${namespace || "default"}`;

  const all = (): Investigation[] =>
    readStore(key).sort((a, b) => b.updatedAt - a.updatedAt);

  return {
    async list() {
      return all();
    },
    async get(id) {
      return all().find((row) => row.id === id) ?? null;
    },
    async create(input) {
      const now = Date.now();
      const investigation: Investigation = {
        id: newId(),
        title: input.title.trim() || "Untitled investigation",
        saga: input.saga ?? null,
        chainId: input.chainId ?? null,
        createdBy: input.createdBy ?? null,
        createdAt: now,
        updatedAt: now,
      };
      writeStore(key, [investigation, ...readStore(key)]);
      return investigation;
    },
    async update(investigation) {
      const next = { ...investigation, updatedAt: Date.now() };
      const rows = readStore(key).map((row) => (row.id === next.id ? next : row));
      if (!rows.some((row) => row.id === next.id)) rows.push(next);
      writeStore(key, rows);
      return next;
    },
    async remove(id) {
      writeStore(
        key,
        readStore(key).filter((row) => row.id !== id),
      );
    },
  };
}
